import * as Sentry from '@sentry/react';
import { env } from './env';

// Scrubbing de tokens de contraseña -----------------------------------------
//
// Con Effetá Apps el PIM ya no tiene contraseñas, pero siguen circulando
// correos viejos con `<FRONT_BASE_URL>/reset-password?token=<token>`: el
// router los manda a la entrada sin el token, y antes de eso la URL ya pasó
// por la carga de la página. Sin filtrarlo termina en `request.url` de
// cualquier evento, en el nombre de la transacción y en los breadcrumbs de
// navegación, al alcance de todo el que tenga lectura en Sentry.
//
// El segmento de `/auth/password-setup/<token>` también se filtra aunque el
// front no llame a esa ruta: la regla cuesta un `indexOf` por evento. Es red
// de contención ante una regresión, no código vivo.
//
// Filtramos en el browser, antes de que el evento salga: breadcrumbs, errores
// y transacciones.

const REDACTED = '[redacted]';

const PASSWORD_SETUP_PREFIX = '/auth/password-setup/';

// Único segmento que puede seguir al prefijo sin ser un token: la ruta
// `POST /auth/password-setup/inspect`. Redactarla borraría el dato útil — qué
// ruta se llamó — sin proteger nada.
const PASSWORD_SETUP_PUBLIC_SEGMENT = 'inspect';

// Reemplaza el segmento que sigue a `/auth/password-setup/` y el valor de
// cualquier query param `token`. Pura: recibe y devuelve string, sin tocar
// nada global. Acepta URLs absolutas, paths y textos que las contengan
// (descriptions de span, mensajes de breadcrumb).
export function scrubSensitiveUrl(value: string): string {
  let out = value;
  const prefixAt = out.indexOf(PASSWORD_SETUP_PREFIX);
  if (prefixAt !== -1) {
    const tokenStart = prefixAt + PASSWORD_SETUP_PREFIX.length;
    // El token termina donde arranca el próximo segmento, la query, el hash
    // o un espacio (caso "GET https://… 200").
    const tokenEnd = (() => {
      for (let i = tokenStart; i < out.length; i += 1) {
        if ('/?# '.includes(out[i] as string)) return i;
      }
      return out.length;
    })();
    const segment = out.slice(tokenStart, tokenEnd);
    if (segment !== PASSWORD_SETUP_PUBLIC_SEGMENT) {
      out = out.slice(0, tokenStart) + REDACTED + out.slice(tokenEnd);
    }
  }
  return out.replace(/([?&]token=)[^&#\s]*/gi, `$1${REDACTED}`);
}

// Scrub shallow de un record: solo los values string. No recorre en
// profundidad a propósito — `data` de breadcrumbs y spans es plano.
//
// Genérico sobre el tipo de entrada: Sentry 10 tipa el `data` de un span como
// `SpanAttributes` (values acotados), no como `Record<string, unknown>`, y
// devolver el tipo ancho rompía la asignación. Solo se reemplazan strings por
// strings, así que el tipo de salida es el mismo que el de entrada.
function scrubDataRecord<T extends Record<string, unknown>>(data: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    out[key] = typeof value === 'string' ? scrubSensitiveUrl(value) : value;
  }
  return out as T;
}

// Breadcrumb de fetch/xhr/navigation: la URL vive en `data.url` (o `data.to`
// y `data.from` en las de navegación) y a veces en el `message`.
export function scrubSentryBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb {
  const scrubbed: Sentry.Breadcrumb = { ...breadcrumb };
  if (typeof scrubbed.message === 'string') {
    scrubbed.message = scrubSensitiveUrl(scrubbed.message);
  }
  if (scrubbed.data) {
    scrubbed.data = scrubDataRecord(scrubbed.data);
  }
  return scrubbed;
}

// Evento de error o de transacción. Cubre `request.url` (la URL de la página,
// que en `/reset-password?token=…` lleva el token), el nombre de la
// transacción, los spans (`description` + `data`, ahí vive `http.url`), el
// `data` del trace context y los breadcrumbs adjuntos.
export function scrubSentryEvent<T extends Sentry.Event>(event: T): T {
  const scrubbed: T = { ...event };

  if (typeof scrubbed.transaction === 'string') {
    scrubbed.transaction = scrubSensitiveUrl(scrubbed.transaction);
  }

  if (scrubbed.request?.url) {
    scrubbed.request = {
      ...scrubbed.request,
      url: scrubSensitiveUrl(scrubbed.request.url),
    };
  }

  if (scrubbed.spans) {
    scrubbed.spans = scrubbed.spans.map((span) => ({
      ...span,
      ...(typeof span.description === 'string' && {
        description: scrubSensitiveUrl(span.description),
      }),
      ...(span.data && { data: scrubDataRecord(span.data) }),
    }));
  }

  const traceData = scrubbed.contexts?.trace?.data;
  if (scrubbed.contexts?.trace && traceData) {
    scrubbed.contexts = {
      ...scrubbed.contexts,
      trace: { ...scrubbed.contexts.trace, data: scrubDataRecord(traceData) },
    };
  }

  if (scrubbed.breadcrumbs) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map(scrubSentryBreadcrumb);
  }

  return scrubbed;
}

/**
 * Inicializa Sentry. No-op si no hay DSN configurado.
 * Usa breadcrumbs de fetch + clicks por default. Replay deshabilitado en MVP
 * (privacy: app interna, datos sensibles).
 */
export function initSentry(): void {
  if (!env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_SENTRY_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Trazas: 10% en prod, 100% en dev/staging para debug.
    tracesSampleRate: env.VITE_SENTRY_ENV === 'production' ? 0.1 : 1.0,
    // No queremos enviar PII salvo que sea necesario para debug.
    sendDefaultPii: false,
    // Tokens de password-setup / reset fuera de breadcrumbs, errores y
    // transacciones. Ver `scrubSensitiveUrl`.
    beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
    beforeSend: (event) => scrubSentryEvent(event),
    beforeSendTransaction: (event) => scrubSentryEvent(event),
  });
}

/**
 * Captura una excepción manualmente con tags opcionales.
 * Wrap fino para no atar al SDK directamente desde features.
 */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}
